import { COLORS } from '../../constants/theme';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { renderFuzzyValueDouble } from '../../utils/fuzzyValue';
import { isProtected } from '../../utils/protection';
import { classifyEffect } from '../../utils/effectClassification';
import { formatEffectDuration } from '../../utils/effectDuration';
import { formatDuration } from '../../utils/formatDuration';
import { latestReportTick } from '../../utils/dossierFreshness';
import { FreshnessCell } from './FreshnessCell';
import { ProtectedBadge } from '../ProtectedBadge';
import { DossierFlavorChip } from './DossierFlavorChip';
import { raceChip, rankChip, personalityChip } from '../../utils/dossierFlavor';
import type { DossierProvinceRow } from '../../types/dossier';
import type { CrystalBallData, SpyOnMilitaryData, InfiltrateData, ActiveEffect } from '../../types/intel';
import type { DossierTabId } from '../../utils/dossierTotals';

interface MobileDossierRosterProps {
  rows: DossierProvinceRow[];
  activeTab: DossierTabId;
  currentTick: number;
  onSelectMember: (member: DossierProvinceRow) => void;
}

/**
 * SF-156 Component 4: one roster row per `DossierProvinceRow`, rendering the shared name/location/
 * badges/freshness header, the always-visible Land/NW hero line, the Race/Rank/Personality chip
 * row, and the active tab's body. Pure prop-consumer — sorting/filtering happen upstream in
 * `MobileDossierShell`.
 */
export function MobileDossierRoster({ rows, activeTab, currentTick, onSelectMember }: MobileDossierRosterProps) {
  return (
    <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {rows.map((row, idx) => (
        <DossierRosterRow
          key={row.provinceId}
          row={row}
          activeTab={activeTab}
          currentTick={currentTick}
          onSelect={() => onSelectMember(row)}
          alt={idx % 2 === 1}
        />
      ))}
    </div>
  );
}

function cbData(row: DossierProvinceRow): CrystalBallData | null {
  return (row.latestCb?.data as CrystalBallData | undefined) ?? null;
}

function somData(row: DossierProvinceRow): SpyOnMilitaryData | null {
  return (row.latestSom?.data as SpyOnMilitaryData | undefined) ?? null;
}

function infiltrateData(row: DossierProvinceRow): InfiltrateData | null {
  return (row.latestInfiltrate?.data as InfiltrateData | undefined) ?? null;
}

interface RowProps {
  row: DossierProvinceRow;
  activeTab: DossierTabId;
  currentTick: number;
  onSelect: () => void;
  alt: boolean;
}

function DossierRosterRow({ row, activeTab, currentTick, onSelect, alt }: RowProps) {
  const freshnessTick = latestReportTick(row);
  const protectedNow = isProtected(row.protectionExpiresAtTick, currentTick);
  const cb = cbData(row);

  return (
    <button
      type="button"
      data-member-id={row.provinceId}
      onClick={onSelect}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: alt ? COLORS.tableRowAlt : COLORS.card,
        border: 'none',
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '9px 12px',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text.primary }}>
          {row.provinceName}
          <span style={{ color: COLORS.text.muted, fontSize: 11, marginLeft: 3 }}>({row.location})</span>
          {row.isMonarch && (
            <span style={{ color: COLORS.negative, fontSize: 10, fontWeight: 700, marginLeft: 6 }}>♛ Monarch</span>
          )}
          {protectedNow && (
            <span style={{ marginLeft: 6, display: 'inline-block', verticalAlign: 'middle' }}>
              <ProtectedBadge protectionExpiresAtTick={row.protectionExpiresAtTick} currentTick={currentTick} />
            </span>
          )}
        </span>
        <FreshnessCell capturedAtTick={freshnessTick} currentTick={currentTick} />
      </div>

      <div style={{ display: 'flex', gap: 14, marginTop: 3, alignItems: 'baseline' }}>
        <IdStat label="Land" value={formatNumber(row.land)} unit="ac" />
        <IdStat label="NW" value={formatNumber(row.networth)} unit="gc" color={COLORS.gold} />
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
        <DossierFlavorChip meta={raceChip(row.race)} />
        <DossierFlavorChip meta={rankChip(row.rank)} />
        {cb && <DossierFlavorChip meta={personalityChip(cb.personality)} />}
      </div>

      {activeTab === 'resources' && <ResourcesBody row={row} />}
      {activeTab === 'effects' && <EffectsBody row={row} />}
      {activeTab === 'military' && <MilitaryBody row={row} />}
    </button>
  );
}

function IdStat({ label, value, unit, color }: { label: string; value: string; unit: string; color?: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
      <span style={{ fontSize: 10, color: COLORS.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</span>
      <span style={{ fontSize: 14.5, fontWeight: 800, color: color ?? COLORS.text.primary }}>{value}</span>
      <span style={{ fontSize: 10, color: COLORS.text.muted }}>{unit}</span>
    </span>
  );
}

function StatCell({ label, value, color, dim }: { label: string; value: string; color?: string; dim?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: dim ? 11 : 11.5, gap: 10 }}>
      <span style={{ color: COLORS.text.tertiary }}>{label}</span>
      <span style={{ color: dim ? COLORS.text.secondary : (color ?? COLORS.text.primary), fontWeight: dim ? 500 : 700 }}>{value}</span>
    </div>
  );
}

function NoDataLine({ text }: { text: string }) {
  return (
    <div style={{ fontSize: 11.5, color: COLORS.text.muted, fontStyle: 'italic', marginTop: 4 }}>{text}</div>
  );
}

function ResourcesBody({ row }: { row: DossierProvinceRow }) {
  const cb = cbData(row);
  const inf = infiltrateData(row);

  if (!cb && !inf) {
    return <NoDataLine text="No Crystal Ball or Infiltrate intel on file — tap to gather." />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 5, maxWidth: 200 }}>
      {cb && <StatCell label="Gold" value={`${formatNumber(cb.money)}gc`} color={COLORS.gold} />}
      {cb && <StatCell label="Runes" value={formatNumber(cb.runes)} color="#b89fe0" />}
      {cb && <StatCell label="Food" value={formatNumber(cb.food)} />}
      {cb && (
        <StatCell
          label="Trade Bal."
          value={`${cb.tradeBalance >= 0 ? '+' : ''}${formatNumber(cb.tradeBalance)}`}
          color={cb.tradeBalance >= 0 ? COLORS.positive : COLORS.negative}
          dim
        />
      )}
      {inf && <StatCell label="TPA" value={inf.rawTPA.toFixed(2)} color="#e0923c" dim />}
    </div>
  );
}

const EFFECT_CATEGORY_COLOR = {
  DEFENSE: COLORS.negative,
  DEBUFF: COLORS.positive,
  BUFF: COLORS.warning,
} as const;

function EffectChip({ effect }: { effect: ActiveEffect }) {
  const category = classifyEffect(effect.effectType);
  const color = EFFECT_CATEGORY_COLOR[category];
  return (
    <span
      style={{
        fontSize: 9.5,
        padding: '1px 5px',
        borderRadius: 3,
        border: `1px solid ${color}`,
        color,
      }}
    >
      {formatEnumLabel(effect.effectType)} ({formatEffectDuration(effect)})
    </span>
  );
}

function EffectsBody({ row }: { row: DossierProvinceRow }) {
  const cb = cbData(row);
  const effects = cb?.activeEffects ?? [];
  if (effects.length === 0) {
    return <NoDataLine text="No active effects on file." />;
  }
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
      {effects.map((e, i) => (
        <EffectChip key={`${e.effectType}-${i}`} effect={e} />
      ))}
    </div>
  );
}

function UnitCell({ label, value }: { label: string; value: number | null | undefined }) {
  return <StatCell label={label} value={value != null ? formatNumber(value) : '—'} dim />;
}

function MilitaryBody({ row }: { row: DossierProvinceRow }) {
  const cb = cbData(row);
  const som = somData(row);

  return (
    <div style={{ display: 'flex', gap: 18, marginTop: 6, alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '0 0 auto' }}>
        <HeroStat label="Off." value={row.modifiedOffense ? renderFuzzyValueDouble(row.modifiedOffense) : '—'} color="#e8a04f" />
        <HeroStat label="Def." value={row.modifiedDefense ? renderFuzzyValueDouble(row.modifiedDefense) : '—'} color={COLORS.link} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
        <UnitCell label="Generals" value={som?.generals} />
        <UnitCell label="Soldiers" value={cb?.soldiers} />
        <UnitCell label="D. Specs" value={cb?.defenseSpecialists} />
        <UnitCell label="O. Specs" value={cb?.offenseSpecialists} />
        <UnitCell label="Elites" value={cb?.elites} />
        <UnitCell label="War Horses" value={cb?.warHorses} />
        <UnitCell label="Prisoners" value={cb?.prisoners} />
        {!cb && <NoDataLine text="No Crystal Ball intel — unit breakdown unavailable." />}
        {som && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 5 }}>
            <ArmyChip out={som.hasArmiesOut} returnTicks={som.nextReturnTicks} />
            <span style={{ fontSize: 11, color: COLORS.text.tertiary }}>
              OME {som.offensiveME != null ? `${(som.offensiveME * 100).toFixed(0)}%` : '—'} · DME {(som.defensiveME * 100).toFixed(0)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function HeroStat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 10.5, color: COLORS.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      <span style={{ fontSize: 16, fontWeight: 800, color }}>{value}</span>
    </div>
  );
}

function ArmyChip({ out, returnTicks }: { out: boolean; returnTicks: number | null }) {
  const color = out ? COLORS.warning : COLORS.positive;
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 3, border: `1px solid ${color}`, color }}>
      {out ? `OUT · ret. ${returnTicks != null ? formatDuration(returnTicks) : '—'}` : 'HOME'}
    </span>
  );
}
